import { useFilters, useSortBy, useTable } from 'react-table';
import React, { ReactElement, useEffect, useState } from 'react';
import { Button, Col, OverlayTrigger, Popover, Row, Table } from 'react-bootstrap';
import { BiArrowToTop, BiCaretDown, BiCaretUp, BiDotsHorizontalRounded } from 'react-icons/all';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import AffichageChargement from './AffichageChargement';
import {
    InstanceDeColonne,
    OptionsDeTable,
    PropsDeTable,
    PropsDeTableSimple,
    PropsDeTableSimpleAvecLoading,
} from './TypesTableSimple';
import { State } from 'history';

export function MyTable<T extends Object>({
    data,
    colonnes,
    lien,
    hover,
    affichageFiltres,
    affichageMenu,
    etatInitial,
    cantNavigate,
}: PropsDeTableSimple<T>): ReactElement {
    const [idsTableauEntitesTries, setIdsTableauEntitesTries] = useState<string[]>([]);

    const navigate = useNavigate();
    const location = useLocation();
    const [showPopover, setShowPopover] = useState<string | null>(null);

    const scrollTop = () => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const options: OptionsDeTable<T> = {
        initialState: etatInitial,
        columns: colonnes,
        data: (data as T[]) ?? [],
        autoResetFilters: false,
        autoResetSortBy: false,
    };

    const table = useTable(options, useFilters, useSortBy);

    const { getTableProps, getTableBodyProps, headerGroups, rows, prepareRow } = (table as unknown) as PropsDeTable<T>;

    useEffect(() => {
        setIdsTableauEntitesTries(rows.map((row) => row.original['id']));
    }, [rows]);

    /**
     * Lorsqu'on clique n'importe où sur une rangée du tableau sauf les petites icônes,
     * aller sur la page de l'entité. Envoyer la position de l'entité dans le tableau
     * ainsi que la liste d'entité pour pouvoir naviguer à partir du formulaire
     *
     * @param target élément html cliqué
     * @param id le id de l'entité cliqué de la rangée du tableau
     * @returns void
     */
    function gererClicRangee(target: EventTarget, id: string): void {
        const html = target as HTMLElement;
        if (html.nodeName === 'svg' || html.nodeName === 'path') return;

        const indexTableauEntite: number = idsTableauEntitesTries.indexOf(id);
        const listeLiens: string[] = idsTableauEntitesTries.map((id) => `${location.pathname}/${id}`);

        const state: State = {
            pos: indexTableauEntite,
            tableauEntite: listeLiens,
        };

        navigate(`./${id}`, { state: state });
    }

    return (
        <>
            <Row className="mb-2">
                <Col>{affichageFiltres?.({ table })}</Col>
                <Col xs="auto">{rows.length} resultats</Col>
            </Row>
            <Table hover={hover} {...getTableProps} style={{ borderCollapse: 'separate', borderSpacing: 0 }}>
                <thead>
                    {headerGroups.map((headerGroup, index) => (
                        <tr {...headerGroup.getHeaderGroupProps()} key={index}>
                            {headerGroup.headers.map((c, index) => {
                                const column = (c as unknown) as InstanceDeColonne<T>;
                                return (
                                    <th
                                        {...column.getHeaderProps(column.getSortByToggleProps())}
                                        className="position-sticky bg-white"
                                        style={{ top: 55 }}
                                        key={index}
                                    >
                                        {column.render('Header')}
                                        <span>
                                            {column.isSorted ? (
                                                column.isSortedDesc ? (
                                                    <BiCaretDown />
                                                ) : (
                                                    <BiCaretUp />
                                                )
                                            ) : (
                                                ''
                                            )}
                                        </span>
                                    </th>
                                );
                            })}
                            {affichageMenu ? (
                                <th
                                    className="position-sticky bg-white py-2"
                                    style={{ width: '0.1%', whiteSpace: 'nowrap', top: 55 }}
                                >
                                    <Button size="sm" variant="link" onClick={scrollTop}>
                                        <BiArrowToTop />
                                    </Button>
                                </th>
                            ) : null}
                        </tr>
                    ))}
                </thead>
                <tbody {...getTableBodyProps()}>
                    {rows.map((row, index) => {
                        prepareRow(row);
                        return (
                            <tr {...row.getRowProps()} className="cursor-pointer" key={index}>
                                {row.cells.map((cell, index) => {
                                    return (
                                        <td
                                            {...cell.getCellProps()}
                                            key={index}
                                            onClick={(event) => {
                                                if (!cantNavigate) {
                                                    gererClicRangee(event.target, row.original['id']);
                                                }
                                            }}
                                        >
                                            {index == 0 && lien ? <Link to={String(row.original[lien])}></Link> : null}
                                            {cell.render('Cell')}
                                        </td>
                                    );
                                })}
                                {affichageMenu ? (
                                    <td className="py-2">
                                        <OverlayTrigger
                                            trigger="click"
                                            rootClose
                                            placement="bottom-end"
                                            show={showPopover === row.id}
                                            onToggle={(isOpen) => setShowPopover(isOpen ? row.id : null)}
                                            overlay={
                                                <Popover id={row.id}>
                                                    <Popover.Content
                                                        className="px-0 py-1"
                                                        onClick={() => setShowPopover(null)}
                                                    >
                                                        {affichageMenu(row.original, index)}
                                                    </Popover.Content>
                                                </Popover>
                                            }
                                        >
                                            <Button
                                                size="sm"
                                                variant="link"
                                                className="text-dark"
                                                onClick={(e) => e.preventDefault()}
                                            >
                                                <BiDotsHorizontalRounded size="1.5em" />
                                            </Button>
                                        </OverlayTrigger>
                                    </td>
                                ) : null}
                            </tr>
                        );
                    })}
                </tbody>
            </Table>
        </>
    );
}

/**
 * Tableau affiché dans le dashboard pour lister les entités
 *
 * @param props props pour le tableau
 * @returns html d'un tableau d'entité
 */
// eslint-disable-next-line @typescript-eslint/ban-types
export default function TableSimple<T extends Object>(props: PropsDeTableSimpleAvecLoading<T>): ReactElement {
    const { resultat, colonnes, lien, hover, affichageFiltres, affichageMenu, etatInitial, cantNavigate } = props;

    return (
        <AffichageChargement
            query={resultat}
            propsToPass={{ colonnes, lien, hover, affichageFiltres, affichageMenu, etatInitial, cantNavigate }}
        >
            {MyTable}
        </AffichageChargement>
    );
}
